import { BuildOutlined, EnvironmentOutlined, MailOutlined, UploadOutlined } from '@ant-design/icons'
import { NetworkStatus } from '@apollo/client'
import { Button, Empty, Image, Input, Select, Spin, Tooltip, Typography, message } from 'antd'
import cls from 'classnames'
import { last } from 'lodash'
import React from 'react'
import { useTranslation } from 'react-i18next'

import { FileUploadButton } from '@/components/file-upload-button/file-upload-button.component'
import {
  LocalFileFragment,
  useGetUserPageInfoQuery,
  useProfilePhotoUpdateMutation,
  useUserInfoHeaderUpdateMutation,
} from '@/generated/graphql'
import { useLayoutSize } from '@/hooks/layout-size.hooks'
import { Address, Cities, Floors } from '@/shares/i18n-mapping'
import { ga, traceEvent } from '@/utils/ga'

import s from './user-info-header.module.less'

export const UserInfoHeader: React.FC<{ userId: number; readonly: boolean }> = ({ userId, readonly, children }) => {
  const { t } = useTranslation()

  const [files, setFiles] = React.useState<LocalFileFragment[]>([])
  const [isDone, setIsDone] = React.useState(true)
  const [locationInfo, setLocationInfo] = React.useState<{ city?: string; address?: string; floor?: string }>({})
  const [seatNumber, setSeatNumber] = React.useState('')

  const ref = React.useRef({
    city: React.useRef<HTMLDivElement>(null),
    address: React.useRef<HTMLDivElement>(null),
    floor: React.useRef<HTMLDivElement>(null),
  })

  const [focus, setFocus] = React.useState({ city: false, address: false, floor: false, seatNumber: false })

  const { isMobileSize } = useLayoutSize()

  const {
    data,
    loading: updateLoading,
    networkStatus,
    refetch,
  } = useGetUserPageInfoQuery({
    fetchPolicy: 'cache-and-network',
    nextFetchPolicy: 'cache-first',
    variables: {
      userId,
      viewedUserPagination: {
        last: 500,
      },
    },
  })

  const [photoUpdate] = useProfilePhotoUpdateMutation({
    onCompleted() {
      const category = traceEvent.profile
      ga('event', category.events.click_upsert_about_me, {
        event_category: category.event_category,
        event_label: 'Photo',
      })
      setIsDone(true)
      refetch()
    },
    onError() {
      setIsDone(true)
    },
  })

  const [userInfoHeaderUpdate] = useUserInfoHeaderUpdateMutation({
    onCompleted() {
      const category = traceEvent.profile
      ga('event', category.events.click_upsert_about_me, {
        event_category: category.event_category,
        event_label: '座位',
      })
      refetch()
    },
  })

  const loading = updateLoading || networkStatus === NetworkStatus.refetch

  React.useEffect(() => {
    if (data) {
      const city = data.user.companyProfile.location.value
      const address = data.user.companyProfile.address
      const floor = data.user.companyProfile.floor
      const seatNumber = data.user.basicProfile.seatNumber.value

      setLocationInfo({ city, address, floor })
      setSeatNumber(seatNumber)
    }
  }, [data])

  React.useEffect(() => {
    if (!readonly && files.length > 0) {
      photoUpdate({
        variables: {
          userId,
          fileId: last(files)!.fileId,
          visible: userInfo.basicProfile.photo.isVisible,
        },
      })
    }
  }, [files])

  if (!data) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
        {loading ? <Spin delay={300} size='large' /> : <Empty />}
      </div>
    )
  }

  const userInfo = data.user
  const departments = userInfo.departments.slice()
  const basicProfile = userInfo.basicProfile
  const companyProfile = userInfo.companyProfile
  const userCity = companyProfile.location.value
  const userAddress = companyProfile.address
  const userFloor = companyProfile.floor
  const userSeatNumber = basicProfile.seatNumber.value

  const photoUrl = basicProfile.photoFile?.url || userInfo.avatarUrl

  const emailAndLocation = (
    <>
      <div className={s.email}>
        <Tooltip title={t('user.info.email')}>
          <MailOutlined />
        </Tooltip>
        {userInfo.email}
      </div>
      <div className={cls(s.location, { [s.locationReadonly]: readonly })}>
        <Tooltip title={t('user.info.location')}>
          <EnvironmentOutlined />
        </Tooltip>
        {readonly || isMobileSize ? (
          [
            t(Cities[userCity] || ''),
            Address[userCity]?.[userAddress] || '',
            Floors[userAddress]?.[userFloor] || '',
            userSeatNumber,
          ]
            .filter(Boolean)
            .join(' - ')
        ) : (
          <>
            <div ref={ref.current.city}>
              <Select
                size='small'
                showArrow={false}
                bordered={focus.city}
                placeholder={t('user.info.modal.city')}
                dropdownMatchSelectWidth={false}
                getPopupContainer={() => ref.current.city.current || document.body}
                value={locationInfo?.city || undefined}
                style={{ fontSize: 15 }}
                onFocus={() => setFocus((prev) => ({ ...prev, city: true }))}
                onBlur={() => setFocus((prev) => ({ ...prev, city: false }))}
                onSelect={(value: any) => {
                  const city = value
                  const address = Object.entries(Address[city])[0][0]
                  const floor = Object.entries(Floors[address])[0][0]
                  setLocationInfo({ city, address, floor })
                  userInfoHeaderUpdate({
                    variables: {
                      input: {
                        address,
                        floor,
                        location: city,
                      },
                    },
                  })
                }}
                options={Object.entries(Cities).map(([key, val]) => ({
                  label: t(val),
                  value: key,
                }))}
              />
            </div>
            <span style={{ margin: '0 4px' }}>{'-'}</span>
            <div ref={ref.current.address}>
              <Select
                size='small'
                showArrow={false}
                bordered={focus.address}
                placeholder={t('user.info.modal.address')}
                dropdownMatchSelectWidth={false}
                getPopupContainer={() => ref.current.address.current || document.body}
                value={locationInfo?.address || undefined}
                style={{ fontSize: 15 }}
                onFocus={() => setFocus((prev) => ({ ...prev, address: true }))}
                onBlur={() => setFocus((prev) => ({ ...prev, address: false }))}
                onSelect={(value: any) => {
                  const address = value
                  const floor = Object.entries(Floors[address])[0][0]
                  setLocationInfo((prev) => ({
                    ...prev,
                    address,
                    floor,
                  }))

                  userInfoHeaderUpdate({
                    variables: {
                      input: {
                        address,
                        floor,
                      },
                    },
                  })
                }}
                options={Object.entries(Address[locationInfo?.city || ''] || {}).map(([key, val]: any) => ({
                  label: val,
                  value: key,
                }))}
              />
            </div>
            <span style={{ margin: '0 4px' }}>{'-'}</span>
            <div ref={ref.current.floor}>
              <Select
                size='small'
                showArrow={false}
                bordered={focus.floor}
                placeholder={t('user.info.modal.floor')}
                dropdownMatchSelectWidth={false}
                getPopupContainer={() => ref.current.floor.current || document.body}
                value={locationInfo?.floor || undefined}
                style={{ fontSize: 15 }}
                onFocus={() => setFocus((prev) => ({ ...prev, floor: true }))}
                onBlur={() => setFocus((prev) => ({ ...prev, floor: false }))}
                onSelect={(value: any) => {
                  const floor = value
                  setLocationInfo((prev) => ({
                    ...prev,
                    floor,
                  }))

                  userInfoHeaderUpdate({
                    variables: {
                      input: {
                        floor,
                      },
                    },
                  })
                }}
                options={Object.entries(Floors[locationInfo?.address || ''] || {}).map(([key, val]: any) => ({
                  label: val,
                  value: key,
                }))}
              />
            </div>
            <span style={{ margin: '0 4px' }}>{'-'}</span>
            <Input
              size='small'
              bordered={focus.seatNumber}
              placeholder={t('profile.basic.seatNumber')}
              value={seatNumber}
              style={{ fontSize: 15, maxWidth: 200 }}
              onFocus={() => setFocus((prev) => ({ ...prev, seatNumber: true }))}
              onChange={(e) => setSeatNumber(e.target.value)}
              onBlur={() => {
                setFocus((prev) => ({ ...prev, seatNumber: false }))
                userInfoHeaderUpdate({
                  variables: {
                    input: {
                      seatNumber,
                    },
                  },
                })
              }}
            />
          </>
        )}
      </div>
    </>
  )

  return (
    <div className={s.header}>
      {(!readonly || basicProfile.photo.isVisible) && (
        <div className={s.photo}>
          {!readonly && (
            <>
              <Tooltip title={t('user.info.photo.upload')} key='upload'>
                <FileUploadButton
                  onClick={() => setIsDone(false)}
                  onValidation={(file) => {
                    if (!file.type.includes('image')) {
                      return { errorInfo: '仅支持上传图片类型', errorMessage: 'Image Only' }
                    }
                    return { errorInfo: '', errorMessage: '' }
                  }}
                  onComplete={() => {
                    message.success(t('post.editor.uploadSucceed'))
                  }}
                  name='profilePhoto'
                  isDone={isDone}
                  setFiles={setFiles}
                  showFiles={false}
                >
                  <Button className={s.upload} size='small' type='primary' ghost icon={<UploadOutlined />} />
                </FileUploadButton>
              </Tooltip>
            </>
          )}
          <div className={s.photoImage}>
            <Image
              width={180}
              height={180}
              style={{ objectFit: 'contain' }}
              src={photoUrl}
              preview={{
                src: photoUrl,
              }}
            />
          </div>
        </div>
      )}

      <div className={s.info}>
        <div className={s.userInfo}>
          <div className={s.nameSection}>
            <div className={s.nameAndStatus}>
              <Typography.Title className={s.name} level={1}>{`${userInfo.name}${
                userInfo.alias ? ` (${userInfo.alias})` : ''
              }`}</Typography.Title>
            </div>
          </div>
          <div className={s.departments}>
            <Tooltip title={t('profile.company.departmentName')}>
              <BuildOutlined />
            </Tooltip>
            {departments
              ?.reverse()
              .map((d) => d.name)
              .join(' - ')}
          </div>
          {emailAndLocation}
        </div>
        {children}
      </div>
    </div>
  )
}
